import { z } from "zod";
import { isoDateTimeString, uuidSchema, pagedResponseSchema } from "./common";
import { PLAN_CODES } from "../catalogs/plans";
import { orgPlanStatusSchema } from "./subscriptions";

// ----------------------------------------------------------------------------
// Plan gift codes ("mã quà tặng") — a concierge way to grant an org a paid plan
// for a custom length of time (e.g. PRO for 180 days) with no purchase. Admin
// issues a code FOR ONE org; the org owner redeems it once. Quotas come from the
// live SubscriptionPlan row (so it's "y chang" that plan, quota still resets per
// the plan's own 30-day period); only the plan's expiry runs for `durationDays`.
// ----------------------------------------------------------------------------

export const planGiftCodeStatusSchema = z.enum([
  "ACTIVE",
  "REDEEMED",
  "REVOKED",
]);
export type PlanGiftCodeStatus = z.infer<typeof planGiftCodeStatusSchema>;

// Gift-code length: 1 day … 3 years. Kept generous so admin can hand out short
// trials or multi-year grants; the UI defaults to 180 (6 tháng).
const giftDurationSchema = z.number().int().min(1).max(1095);

// The typed-in code. Uppercase alnum + dashes, e.g. CHOTCA-A1B2-C3D4. Max 40 so
// it fits the DB column and rejects garbage paste.
export const giftCodeStringSchema = z
  .string()
  .trim()
  .min(4)
  .max(40)
  .regex(/^[A-Z0-9-]+$/, "Mã chỉ gồm chữ IN HOA, số và dấu gạch ngang");

// -------- Employer: redeem --------

// Redeem a gift code for the org. The org is taken from the route; only the code
// is in the body. LEGAL parity with subscribe isn't needed — it's a free grant,
// not a purchase — so no consent field here.
export const redeemGiftCodeRequestSchema = z.object({
  code: giftCodeStringSchema,
});
export type RedeemGiftCodeRequest = z.infer<typeof redeemGiftCodeRequestSchema>;

// After a successful redeem, return the org's new plan status so the FE can
// refresh the "gói" screen straight away (same shape the status endpoint uses).
export const redeemGiftCodeResponseSchema = z.object({
  status: orgPlanStatusSchema,
  // The plan + how long was granted, for the success toast ("Đã kích hoạt PRO
  // 180 ngày").
  planCode: z.enum(PLAN_CODES),
  durationDays: z.number().int(),
});
export type RedeemGiftCodeResponse = z.infer<
  typeof redeemGiftCodeResponseSchema
>;

// -------- Admin: create --------

// Issue a gift code for a single org. One code per request — the server
// generates the random code string.
export const adminCreateGiftCodesRequestSchema = z.object({
  orgId: uuidSchema,
  planCode: z.enum(PLAN_CODES),
  durationDays: giftDurationSchema,
  // Optional redemption deadline (null/omit = no deadline). ISO datetime.
  redeemBy: isoDateTimeString.nullable().optional(),
  note: z.string().trim().max(500).optional(),
});
export type AdminCreateGiftCodesRequest = z.infer<
  typeof adminCreateGiftCodesRequestSchema
>;

// -------- Shared row DTO --------

// One gift code as the admin list shows it. `orgName` is joined for display.
export const planGiftCodeSchema = z.object({
  id: uuidSchema,
  code: z.string(),
  orgId: uuidSchema,
  orgName: z.string(),
  planCode: z.enum(PLAN_CODES),
  durationDays: z.number().int(),
  status: planGiftCodeStatusSchema,
  redeemBy: isoDateTimeString.nullable(),
  redeemedAt: isoDateTimeString.nullable(),
  note: z.string().nullable(),
  createdAt: isoDateTimeString,
});
export type PlanGiftCode = z.infer<typeof planGiftCodeSchema>;

// Response to admin create: the freshly minted codes (so the admin can copy them
// to hand out). Same row shape as the list.
export const adminCreateGiftCodesResponseSchema = z.object({
  items: z.array(planGiftCodeSchema),
});
export type AdminCreateGiftCodesResponse = z.infer<
  typeof adminCreateGiftCodesResponseSchema
>;

// -------- Admin: list --------

export const adminGiftCodeListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
  status: planGiftCodeStatusSchema.optional(),
  orgId: uuidSchema.optional(),
  // Free-text search over code / org name.
  search: z.string().trim().min(1).max(120).optional(),
});
export type AdminGiftCodeListQuery = z.infer<
  typeof adminGiftCodeListQuerySchema
>;

export const adminGiftCodeListResponseSchema =
  pagedResponseSchema(planGiftCodeSchema);
export type AdminGiftCodeListResponse = z.infer<
  typeof adminGiftCodeListResponseSchema
>;
